import { PlusOutlined } from "@ant-design/icons"
import { Button, Divider, Input, Select, Space } from "antd"
import { camelCase, orderBy } from "lodash"
import { useCallback, useMemo, useState } from "react"

import Firebase from "#classes/Firebase"
import { useFirebaseContext } from "#contexts/FirebaseContextProvider"
import { DOC_TYPES } from "#customTypes/composedTypes/firebaseTypes"

type OptionDocType = DOC_TYPES.TAGS | DOC_TYPES.ALLERGIES

interface CreatableOptionSelectProps {
  docType: OptionDocType
  placeholder?: string
  value?: string[]
  onChange?: (values: string[]) => void
}

/**
 * Bridges the gap between when addDocument fires and the Firestore listener
 * updates context. Entries here are merged into optionsByValue for immediate
 * chip display. Once the listener catches up, the duplicate is harmless.
 */
type PendingEntry = { label: string; icon: string }

const CreatableOptionSelect = ({
  docType,
  placeholder,
  value,
  onChange,
}: CreatableOptionSelectProps) => {
  const { tagsByValue, allergiesByValue } = useFirebaseContext()

  const contextOptions =
    docType === DOC_TYPES.TAGS ? tagsByValue : allergiesByValue

  const [pendingOptions, setPendingOptions] = useState<
    Record<string, PendingEntry>
  >({})
  const [newLabel, setNewLabel] = useState("")
  const [newIcon, setNewIcon] = useState("")
  const [searchValue, setSearchValue] = useState("")

  const optionsByValue = useMemo(
    () => ({ ...contextOptions, ...pendingOptions }),
    [contextOptions, pendingOptions]
  )

  const selectOptions = useMemo(() => {
    const entries = Object.entries(optionsByValue).map(
      ([val, { label, icon }]) => ({
        value: val,
        label: `${icon} ${label}`.trim(),
        sortKey: label,
      })
    )
    return orderBy(entries, "sortKey")
  }, [optionsByValue])

  const handleChange = useCallback(
    (newValues: string[]) => {
      onChange?.(newValues)
    },
    [onChange]
  )

  const handleAddNew = useCallback(() => {
    const label = newLabel.trim()
    const icon = newIcon.trim()
    if (!label) return

    const generatedValue = camelCase(label)
    const finalValue = generatedValue || label

    if (optionsByValue[finalValue]) return

    setPendingOptions((prev) => ({
      ...prev,
      [finalValue]: { label, icon },
    }))

    Firebase.getInstance().addDocument(docType, {
      id: finalValue,
      label,
      icon,
    })

    onChange?.([...(value ?? []), finalValue])
    setNewLabel("")
    setNewIcon("")
    setSearchValue("")
  }, [newLabel, newIcon, optionsByValue, docType, onChange, value])

  const tagRender = useCallback(
    (props: {
      label: React.ReactNode
      value: string
      closable: boolean
      onClose: () => void
    }) => {
      const { value: val, closable, onClose } = props
      const match = optionsByValue[val]
      const displayLabel = match ? `${match.icon} ${match.label}`.trim() : val

      return (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            padding: "0 7px",
            marginInlineEnd: 4,
            background: "#f5f5f5",
            border: "1px solid #d9d9d9",
            borderRadius: 4,
            lineHeight: "22px",
          }}
        >
          {displayLabel}
          {closable && (
            <span
              style={{ cursor: "pointer", marginLeft: 2 }}
              onClick={onClose}
            >
              x
            </span>
          )}
        </span>
      )
    },
    [optionsByValue]
  )

  const handleSearch = useCallback((text: string) => {
    setSearchValue(text)
    setNewLabel(text)
  }, [])

  const dropdownFooter = useCallback(
    (menu: React.ReactElement) => (
      <>
        {menu}
        <Divider style={{ margin: "8px 0" }} />
        <Space style={{ padding: "0 8px 8px" }} align="center">
          <Input
            placeholder="Name"
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
            style={{ width: 120 }}
            size="small"
          />
          <Input
            placeholder="Icon"
            value={newIcon}
            onChange={(e) => setNewIcon(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
            style={{ width: 45 }}
            size="small"
          />
          <Button
            type="primary"
            size="small"
            icon={<PlusOutlined />}
            onClick={handleAddNew}
            disabled={!newLabel.trim()}
          >
            Add
          </Button>
        </Space>
      </>
    ),
    [newLabel, newIcon, handleAddNew]
  )

  return (
    <Select
      mode="multiple"
      placeholder={placeholder}
      value={value}
      onChange={handleChange}
      options={selectOptions}
      tagRender={tagRender}
      searchValue={searchValue}
      onSearch={handleSearch}
      filterOption={(input, option) =>
        (option?.label as string)
          ?.toLowerCase()
          .includes(input.toLowerCase()) ?? false
      }
      dropdownRender={dropdownFooter}
      style={{ width: "100%" }}
    />
  )
}

export default CreatableOptionSelect
